import { useState } from "react";
import { Plus, Settings2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger, DialogClose,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { useVehicle } from "../context/VehicleContext";

const Num = ({ label, value, onChange, testId, step = "0.1" }) => (
  <label className="block">
    <span className="font-mono text-[10px] tracking-widest uppercase text-slate-400 block mb-1">{label}</span>
    <input type="number" step={step} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
      className="w-full h-9 px-2.5 rounded-md bg-ink border border-steel text-white font-mono text-sm text-center focus:outline-none focus:ring-1 focus:ring-cyan" />
  </label>
);

const EMPTY = {
  make: "", model: "", year: "",
  frontCamber: "0", frontCaster: "3", frontToe: "0.1",
  rearCamber: "0", rearToe: "0", rearAdjustable: false,
};

export const CustomVehicleDialog = () => {
  const { addCustomVehicle } = useVehicle();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = () => {
    if (!f.make.trim() || !f.model.trim()) { toast.error("Enter a make and model."); return; }
    const entry = addCustomVehicle(f);
    toast.success("Custom vehicle added", { description: entry.label });
    setF(EMPTY);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="add-custom-btn"
          className="inline-flex items-center gap-2 rounded-xl bg-elevated border border-steel text-slate-200 px-4 py-2.5 font-mono text-xs uppercase tracking-widest hover:bg-steel transition-colors">
          <Plus size={15} /> Add Custom Vehicle
        </button>
      </DialogTrigger>
      <DialogContent className="bg-surface border-steel text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-widest flex items-center gap-2">
            <Settings2 size={18} className="text-cyan" /> Custom Vehicle
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Enter target angles (°). We set a ±0.5° camber/caster and ±0.05° toe window automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-3">
          <label className="col-span-1 block">
            <span className="font-mono text-[10px] tracking-widest uppercase text-slate-400 block mb-1">Make</span>
            <input value={f.make} onChange={(e) => set("make", e.target.value)} data-testid="custom-make"
              className="w-full h-9 px-2.5 rounded-md bg-ink border border-steel text-white text-sm focus:outline-none focus:ring-1 focus:ring-cyan" />
          </label>
          <label className="col-span-1 block">
            <span className="font-mono text-[10px] tracking-widest uppercase text-slate-400 block mb-1">Model</span>
            <input value={f.model} onChange={(e) => set("model", e.target.value)} data-testid="custom-model"
              className="w-full h-9 px-2.5 rounded-md bg-ink border border-steel text-white text-sm focus:outline-none focus:ring-1 focus:ring-cyan" />
          </label>
          <label className="col-span-1 block">
            <span className="font-mono text-[10px] tracking-widest uppercase text-slate-400 block mb-1">Year</span>
            <input value={f.year} onChange={(e) => set("year", e.target.value)} data-testid="custom-year" placeholder="2024"
              className="w-full h-9 px-2.5 rounded-md bg-ink border border-steel text-white text-sm focus:outline-none focus:ring-1 focus:ring-cyan" />
          </label>
        </div>

        <div className="font-mono text-[10px] text-cyan tracking-widest uppercase mt-2">Front Targets</div>
        <div className="grid grid-cols-3 gap-3">
          <Num label="Camber" value={f.frontCamber} onChange={(v) => set("frontCamber", v)} testId="custom-front-camber" />
          <Num label="Caster" value={f.frontCaster} onChange={(v) => set("frontCaster", v)} testId="custom-front-caster" />
          <Num label="Total Toe" value={f.frontToe} onChange={(v) => set("frontToe", v)} step="0.02" testId="custom-front-toe" />
        </div>

        <div className="font-mono text-[10px] text-cyan tracking-widest uppercase mt-2">Rear Targets</div>
        <div className="grid grid-cols-3 gap-3 items-end">
          <Num label="Camber" value={f.rearCamber} onChange={(v) => set("rearCamber", v)} testId="custom-rear-camber" />
          <Num label="Total Toe" value={f.rearToe} onChange={(v) => set("rearToe", v)} step="0.02" testId="custom-rear-toe" />
          <label className="flex items-center gap-2 h-9 font-mono text-[10px] uppercase tracking-widest text-slate-300 cursor-pointer">
            <input type="checkbox" checked={f.rearAdjustable} onChange={(e) => set("rearAdjustable", e.target.checked)} data-testid="custom-rear-adjustable"
              className="accent-cyan w-4 h-4" />
            Adjustable
          </label>
        </div>

        <DialogFooter className="mt-3">
          <DialogClose asChild>
            <Button variant="outline" className="bg-elevated border-steel text-slate-300 hover:bg-steel hover:text-white font-mono text-xs uppercase tracking-widest">Cancel</Button>
          </DialogClose>
          <Button onClick={submit} data-testid="custom-save-btn"
            className="bg-cyan text-ink hover:bg-cyan/90 font-mono text-xs uppercase tracking-widest font-bold">Add & Activate</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
